import {JSONSchema7} from 'json-schema';

import {Query} from './traceServerClientInterface/query';
type ExtraKeysAllowed = {
  [key: string]: any;
};

export const ComputedCallStatuses = {
  success: 'success' as const,
  error: 'error' as const,
  running: 'running' as const,
  descendant_error: 'descendant_error' as const,
} as const;
export type ComputedCallStatusType = keyof typeof ComputedCallStatuses;

type WeaveSummarySchema = {
  costs?: {[key: string]: LLMCostSchema};
  latency_ms?: number; // latency in milliseconds
  status?: ComputedCallStatusType;
} & ExtraKeysAllowed;

export type LLMUsageSchema = {
  // Should collapse prompt and input? Ideally yes, but
  // back compat is a concern.
  prompt_tokens?: number;
  input_tokens?: number;
  // Should collapse completion and output? Ideally yes, but
  // back compat is a concern.
  completion_tokens?: number;
  output_tokens?: number;
  // Deepseek R1 and other reasoning models. OpenAI-compat providers
  // surface this as completion_tokens_details.reasoning_tokens or
  // output_tokens_details.reasoning_tokens instead.
  thinking_tokens?: number;
  completion_tokens_details?: {
    reasoning_tokens?: number;
  };
  output_tokens_details?: {
    reasoning_tokens?: number;
  };
  // Cache tokens
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
  requests?: number;
  total_tokens?: number;
} & ExtraKeysAllowed;

export type LLMCostSchema = LLMUsageSchema & {
  loading?: boolean;
  // Cost for request
  prompt_tokens_total_cost?: number;
  completion_tokens_total_cost?: number;

  // Cache token costs
  cache_read_input_tokens_total_cost?: number;
  cache_creation_input_tokens_total_cost?: number;

  // Cost per unit
  prompt_token_cost?: number;
  completion_token_cost?: number;
  cache_read_input_token_cost?: number;
  cache_creation_input_token_cost?: number;
  prompt_token_cost_unit?: string;
  completion_token_cost_unit?: string;

  effective_date?: string;
  provider_id?: string;
  pricing_level?: string;
  pricing_level_id?: string;
  created_at?: string;
  created_by?: string;
};

type SummaryInsertMap = {
  usage?: {[key: string]: LLMUsageSchema};
} & ExtraKeysAllowed;

type SummaryMap = {
  weave?: WeaveSummarySchema;
} & SummaryInsertMap;

export type KeyedDictType = {
  [key: string]: any;
  _keys?: string[];
};
export type TraceCallSchema = {
  project_id: string;
  id: string;
  op_name: string;
  display_name?: string;
  trace_id: string;
  parent_id?: string;
  started_at: string;
  attributes: KeyedDictType;
  inputs: KeyedDictType;
  ended_at?: string;
  exception?: string;
  // Using `unknown` for `output` instead of an `any` so that the type checkers
  // force us to handle all possible types. When using `any`, this value can be
  // freely assigned to any other variable without any type checking. This way,
  // we can ensure that we handle all possible types.
  output?: unknown;
  summary?: SummaryMap;
  wb_run_id?: string;
  wb_run_step?: number;
  wb_user_id?: string;
  total_storage_size_bytes?: number;
  thread_id?: string;
  turn_id?: string;
  expire_at?: string;
};
export type TraceCallReadReq = {
  project_id: string;
  id: string;
  include_costs?: boolean;
  include_total_storage_size?: boolean;
};

export type TraceCallReadSuccess = {
  call: TraceCallSchema;
};
export type TraceCallReadError = {
  detail: string;
};
export type TraceCallReadRes = TraceCallReadSuccess | TraceCallReadError;
export interface TraceCallsFilter {
  op_names?: string[];
  input_refs?: string[];
  output_refs?: string[];
  parent_ids?: string[];
  trace_ids?: string[];
  call_ids?: string[];
  trace_roots_only?: boolean;
  wb_run_ids?: string[];
  wb_user_ids?: string[];
  turn_ids?: string[];
}

export type SortBy = {field: string; direction: 'asc' | 'desc'};

export type TraceCallsQueryReq = {
  project_id: string;
  filter?: TraceCallsFilter;
  limit?: number;
  offset?: number;
  sort_by?: SortBy[];
  query?: Query;
  columns?: string[];
  expand_columns?: string[];
  return_expanded_column_values?: boolean;
  include_costs?: boolean;
  include_feedback?: boolean;
  include_storage_size?: boolean;
  include_total_storage_size?: boolean;
};

export type TraceCallsQueryRes = {
  calls: TraceCallSchema[];
};

export type TraceCallsQueryStatsReq = {
  project_id: string;
  filter?: TraceCallsFilter;
  query?: Query;
  limit?: number;
  expand_columns?: string[];
};

export type TraceCallsQueryStatsRes = {
  count: number;
  // True when count saturated the request's limit; render as "<count>+".
  has_more?: boolean;
  total_storage_size_bytes?: number;
};

export type EvalResultsSortBy = SortBy & {
  evaluation_call_id?: string;
  mode?: 'value' | 'difference';
};

export type EvalResultsFilter = {
  evaluation_call_id?: string;
  query: Query;
};

export type EvalResultsQueryReq = {
  evaluation_call_ids?: string[];
  evaluation_run_ids?: string[];
  require_intersection?: boolean;
  include_raw_data_rows?: boolean;
  resolve_row_refs?: boolean;
  include_rows?: boolean;
  include_summary?: boolean;
  include_predict_and_score_children?: boolean;
  summary_require_intersection?: boolean;
  sort_by?: EvalResultsSortBy[];
  filters?: EvalResultsFilter[];
  limit?: number;
  offset?: number;
};

export type GenAISpanRef = {
  trace_id: string;
  span_id: string;
};

export const normalizeGenAISpanRefs = (
  value: GenAISpanRef[] | null | undefined
): GenAISpanRef[] => {
  const seen = new Set<string>();
  return (value ?? []).filter(ref => {
    const key = `${ref.trace_id}/${ref.span_id}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export type EvalResultsTrial = {
  predict_and_score_call_id: string;
  predict_call_id?: string | null;
  model_output?: unknown;
  scores: Record<string, any>;
  model_latency_seconds?: number | null;
  total_tokens?: number | null;
  scorer_call_ids: Record<string, string>;
  genai_span_ref?: GenAISpanRef[] | null;
};

export type EvalResultsRowEvaluation = {
  evaluation_call_id: string;
  trials: EvalResultsTrial[];
};

export type EvalResultsRow = {
  row_digest: string;
  raw_data_row?: unknown;
  evaluations: EvalResultsRowEvaluation[];
};

export type EvalResultsQueryRes = {
  rows: EvalResultsRow[];
  total_rows: number;
  summary?: EvalResultsSummaryRes | null;
};

export type EvalResultsScorerStats = {
  scorer_key: string;
  /** Dot-joined subpath for nested dimensions, e.g. 'passed' for token_distance.passed. */
  path?: string | null;
  /** Type of the leaf value: binary (bool) or continuous (number). */
  value_type?: 'binary' | 'continuous' | 'text' | null;
  trial_count: number;
  numeric_count: number;
  numeric_mean?: number | null;
  pass_true_count: number;
  pass_known_count: number;
  pass_rate?: number | null;
  pass_signal_coverage?: number | null;
};

export type EvalResultsEvaluationSummary = {
  evaluation_call_id: string;
  trial_count: number;
  scorer_stats: EvalResultsScorerStats[];
  evaluation_ref?: string;
  model_ref?: string;
  display_name?: string;
  trace_id?: string;
  started_at?: string;
};

export type EvalResultsSummaryRes = {
  row_count: number;
  evaluations: EvalResultsEvaluationSummary[];
};

export type TraceCallsDeleteReq = {
  project_id: string;
  call_ids: string[];
};

export type TraceCallUpdateReq = {
  project_id: string;
  call_id: string;
  display_name: string;
};

export type FeedbackCreateReq = {
  id?: string;
  project_id: string;
  weave_ref: string;
  creator?: string | null;
  feedback_type: string;
  payload: Record<string, any>;
  annotation_ref?: string | null;
  runnable_ref?: string | null;
  call_ref?: string | null;
  trigger_ref?: string | null;
  queue_id?: string | null;
  wb_user_id?: string | null;
};

export type FeedbackCreateSuccess = {
  id: string;
  created_at: string;
  wb_user_id: string;
  payload: Record<string, any>;
};
export type FeedbackCreateError = {
  detail: string;
};
export type FeedbackCreateRes = FeedbackCreateSuccess | FeedbackCreateError;

export type FeedbackQueryReq = {
  project_id: string;
  query?: Query;
  sort_by?: SortBy[];
  limit?: number;
};

export type Feedback = {
  id: string;
  project_id: string;
  weave_ref: string;
  wb_user_id: string; // authenticated creator username
  creator: string | null; // display name
  created_at: string;
  feedback_type: string;
  payload: Record<string, any>;
  annotation_ref?: string;
  runnable_ref?: string;
  call_ref?: string;
  trigger_ref?: string;
  queue_id?: string;
  // Typed scorer columns. Populated only on `wandb.agent_monitor` rows
  // (enforced server-side in feedback.py). See the Scorer Feedback Schema
  // Proposal: a scorer writes tags OR ratings (or both), with optional
  // reasons/confidences keyed by tag-name / rating-name.
  scorer_tags?: string[];
  scorer_ratings?: Record<string, number>;
  scorer_tag_reasons?: Record<string, string>;
  scorer_tag_confidences?: Record<string, number>;
  scorer_rating_reasons?: Record<string, string>;
  scorer_rating_confidences?: Record<string, number>;
  // Metadata for agent spans, copied from the `spans` table.
  // Provided as a best-effort: the `spans` table remains the source of truth.
  span_agent_name: string;
  span_agent_version: string;
  span_status_code: string;
};

export type FeedbackQuerySuccess = {
  result: Feedback[];
};
export type FeedbackQueryError = {
  detail: string;
};
export type FeedbackQueryRes = FeedbackQuerySuccess | FeedbackQueryError;

export type FeedbackPurgeReq = {
  project_id: string;
  query: Query;
};
export type FeedbackPurgeSuccess = {};
export type FeedbackPurgeError = {
  detail: string;
};
export type FeedbackPurgeRes = FeedbackPurgeSuccess | FeedbackPurgeError;

export type FeedbackReplaceReq = FeedbackCreateReq & {
  feedback_id: string;
};
export type FeedbackReplaceRes = FeedbackCreateRes;

// Feedback stats (aggregated scorer outputs over time)
export type FeedbackMetricSpec = {
  json_path: string;
  value_type?: 'numeric' | 'boolean' | 'categorical';
  aggregations?: string[];
  percentiles?: number[];
};

export type FeedbackStatsReq = {
  project_id: string;
  start: string;
  end?: string;
  granularity?: number;
  timezone?: string;
  feedback_type?: string;
  trigger_ref?: string;
  metrics: FeedbackMetricSpec[];
};

export type FeedbackStatsBucket = {
  timestamp: string;
  count: number;
  [key: string]: string | number | undefined;
};

export type FeedbackWindowStat = {
  avg?: number | null;
  min?: number | null;
  max?: number | null;
  sum?: number | null;
  count?: number | null;
  count_true?: number | null;
  count_false?: number | null;
  p1?: number | null;
  p5?: number | null;
  p50?: number | null;
  p95?: number | null;
  p99?: number | null;
};

export type FeedbackStatsRes = {
  start: string;
  end: string;
  granularity: number;
  timezone: string;
  buckets: FeedbackStatsBucket[];
  window_stats?: Record<string, FeedbackWindowStat>;
};

/**
 * Request payload for POST requests to the /feedback/aggregate endpoint.
 */
export type FeedbackAggregateReq = {
  project_id: string;
  after_ms: number;
  before_ms: number;
  time_bucket_seconds?: number;
  feedback_types?: string[];
  tags?: string[];
  rating_min?: number;
  rating_max?: number;
  monitor_ids?: string[];
  scorer_ids?: string[];
  span_agent_names?: string[];
  span_types?: Array<'agent_turn' | 'agent_conversation'>;
  group_by?: Array<
    'scorer_id' | 'span_agent_name' | 'span_agent_version' | 'span_status_code'
  >;
};

/** One (time bucket, group) row of aggregated scores. */
export type FeedbackAggregateBucket = {
  time_bucket_start_ms: number | null;
  group: Record<string, string>;
  total_count: number;
  scored_count: number;
  tag_counts: Record<string, number>;
  rating_counts: Record<string, number>;
  rating_sums: Record<string, number>;
};

/** Response from the /feedback/aggregate endpoint. */
export type FeedbackAggregateRes = {
  time_bucket_seconds: number | null;
  after_ms: number;
  before_ms: number;
  buckets: FeedbackAggregateBucket[];
};

// Feedback payload schema (discovered paths for stats)
export type FeedbackPayloadPath = {
  json_path: string;
  value_type?: 'numeric' | 'boolean' | 'categorical';
};

export type FeedbackPayloadSchemaReq = {
  project_id: string;
  start: string;
  end?: string;
  feedback_type?: string;
  trigger_ref?: string;
  sample_limit?: number;
};

export type FeedbackPayloadSchemaRes = {
  paths: FeedbackPayloadPath[];
};
interface TraceObjectsFilter {
  base_object_classes?: string[];
  exclude_base_object_classes?: string[];
  leaf_object_classes?: string[];
  object_ids?: string[];
  is_op?: boolean;
  latest_only?: boolean;
  tags?: string[];
  aliases?: string[];
}
export type TraceObjQueryReq = {
  project_id: string;
  filter?: TraceObjectsFilter;
  limit?: number;
  offset?: number;
  sort_by?: SortBy[];
  metadata_only?: boolean;
  include_storage_size?: boolean;
  include_tags_and_aliases?: boolean;
};

export interface TraceObjSchema<
  T extends any = any,
  OBC extends string = string,
> {
  project_id: string;
  object_id: string;
  created_at: string;
  deleted_at: string | null;
  digest: string;
  version_index: number;
  is_latest: number;
  kind: 'op' | 'object';
  base_object_class?: OBC;
  leaf_object_class?: OBC;
  val: T;
  wb_user_id?: string;
  size_bytes?: number;
  tags?: string[];
  aliases?: string[];
}

export type TraceObjQueryRes<T extends any = any> = {
  objs: Array<TraceObjSchema<T>>;
};

export type TraceObjReadReq = {
  project_id: string;
  object_id: string;
  digest: string;
  metadata_only?: boolean;
  include_tags_and_aliases?: boolean;
};

export type TraceObjReadRes = {
  obj: TraceObjSchema;
};

export type TraceObjCreateReq<T extends any = any> = {
  obj: {
    project_id: string;
    object_id: string;
    val: T;
    builtin_object_class?: string;
  };
};

export type TraceObjCreateRes = {
  digest: string;
};

export type TraceObjDeleteReq = {
  project_id: string;
  object_id: string;
  digests?: string[];
};

export type TraceObjDeleteRes = {
  num_deleted?: number;
};

// --- Tags & Aliases ---
export type TraceObjAddTagsReq = {
  project_id: string;
  object_id: string;
  digest: string;
  tags: string[];
};

export type TraceObjAddTagsRes = any;

export type TraceObjRemoveTagsReq = {
  project_id: string;
  object_id: string;
  digest: string;
  tags: string[];
};

export type TraceObjRemoveTagsRes = any;

export type TraceObjSetAliasesReq = {
  project_id: string;
  object_id: string;
  digest: string;
  aliases: string[];
};

export type TraceObjSetAliasesRes = any;

export type TraceObjRemoveAliasesReq = {
  project_id: string;
  object_id: string;
  aliases: string[];
};

export type TraceObjRemoveAliasesRes = any;

export type TraceTagsListReq = {
  project_id: string;
};

export type TraceTagsListRes = {
  tags: string[];
};

export type TraceAliasesListReq = {
  project_id: string;
};

export type TraceAliasesListRes = {
  aliases: string[];
};

export type TraceRefsReadBatchReq = {
  refs: string[];
};

export type TraceRefsReadBatchRes = {
  vals: any[];
};

export type TraceTableQueryReq = {
  project_id: string;
  digest: string;
  filter?: {
    row_digests?: string[];
  };
  limit?: number;
  offset?: number;
  sort_by?: SortBy[];
};

export type TraceTableQueryStatsBatchReq = {
  project_id: string;
  digests: string[];
  include_storage_size?: boolean;
};

export type TraceTableQueryStatsBatchRes = {
  tables: Array<{
    digest: string;
    count: number;
    storage_size_bytes: number;
  }>;
};

export type TraceTableQueryRes = {
  rows: Array<{
    digest: string;
    val: any;
    original_index?: number;
  }>;
};

export type TraceFileContentReadReq = {
  project_id: string;
  digest: string;
};

export type TraceFileContentReadRes = {
  content: ArrayBuffer;
};

export type FilesStatsReq = {
  project_id: string;
};

export type FilesStatsRes = {
  total_size_bytes: number;
};

type JsonObjectResponseFormat = {
  type: 'json_object';
};
type TextResponseFormat = {
  type: 'text';
};
type JsonSchemaResponseFormat = {
  type: 'json_schema';
  json_schema: {
    name: string;
    strict?: boolean;
    schema: JSONSchema7;
  };
};

export type ResponseFormat =
  | JsonSchemaResponseFormat
  | JsonObjectResponseFormat
  | TextResponseFormat;

export type CompletionsCreateInputs = {
  model: string;
  messages: any[];
  temperature?: number;

  // These are optional, depending on the LLM provider some accept these some dont
  stop?: string[];
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  n?: number;
  response_format?: ResponseFormat;
  tools?: any[];
  // These are optional, o3 accepts max_completion_tokens, others accept max_tokens
  max_tokens?: number;
  max_completion_tokens?: number;
  prompt?: string;
  template_vars?: Record<string, string>;
};

export type CompletionsCreateReq = {
  project_id: string;
  inputs: CompletionsCreateInputs;
  track_llm_call?: boolean;
  conversation_id?: string;
  conversation_name?: string;
  source?: string;
};

export type CompletionsCreateRes = {
  response: any;
  weave_call_id?: string; // Deprecated: use span_id
  span_id?: string;
  trace_id?: string;
  conversation_id?: string;
};

// Streaming completions returns NDJSON chunks. Consumers can turn chunks into
// the final response they need. We expose the raw chunk list for maximum
// flexibility.
export type CompletionsCreateStreamReq = CompletionsCreateReq;

export type CompletionChunk = MetaChunk | ContentChunk;

type MetaChunk = {
  _meta: {
    weave_call_id: string; // Deprecated: use span_id
    span_id?: string;
    trace_id?: string;
    conversation_id?: string;
  };
};

export type ContentChunk = {
  id: string;
  created: number;
  model: string;
  object: 'chat.completion.chunk';
  system_fingerprint: string;
  choices: Array<{
    finish_reason: 'stop' | 'tool_calls' | null;
    index: number;
    delta: {
      refusal: null;
      content: string | null;
      reasoning_content: string | null;
      role: string | null;
      function_call: null;
      tool_calls: Array<{
        id: string | null;
        function: {
          arguments: string;
          name: string | null;
        };
        type: 'function';
      }> | null;
      audio: null;
    };
    logprobs: null;
  }>;
  citations: null;
  usage?: {
    completion_tokens: number;
    prompt_tokens: number;
    total_tokens: number;
    completion_tokens_details: {
      accepted_prediction_tokens: number;
      audio_tokens: number;
      reasoning_tokens: number;
      rejected_prediction_tokens: number;
    };
    prompt_tokens_details: {
      audio_tokens: number;
      cached_tokens: number;
    };
  };
};

// Add a type for the accumulated message structure
export type AccumulatedMessage = {
  role: string;
  content: string | null;
  tool_calls?: Array<{
    id: string;
    function: {
      name: string;
      arguments: string;
    };
    type: 'function';
    index: number;
  }>;
};

// Callback invoked whenever a new chunk is parsed during streaming.
export type OnCompletionChunk = (chunk: CompletionChunk) => void;

// Add a type for the streaming response
export type CompletionsCreateStreamRes = {
  chunks: CompletionChunk[];
  weave_call_id?: string; // Deprecated: use span_id
  span_id?: string;
  trace_id?: string;
  conversation_id?: string;
};

export type ProjectStatsReq = {
  project_id: string;
};

export type ProjectStatsRes = {
  trace_storage_size_bytes: number;
  objects_storage_size_bytes: number;
  tables_storage_size_bytes: number;
  files_storage_size_bytes: number;
};

export type ProjectTTLSettingsReadReq = {
  project_id: string;
};

export type ProjectTTLSettingsReadRes = {
  retention_days: number | null; // null = no TTL (infinite retention)
};

export type ProjectTTLSettingsUpdateReq = {
  project_id: string;
  retention_days: number | null; // null disables TTL; must be null or >= 1
};

export type ProjectTTLSettingsUpdateRes = {
  retention_days: number | null;
};

export enum ContentType {
  csv = 'text/csv',
  tsv = 'text/tab-separated-values',
  any = '*/*',
  jsonl = 'application/jsonl',
  json = 'application/json',
}

export const fileExtensions = {
  [ContentType.csv]: 'csv',
  [ContentType.tsv]: 'tsv',
  [ContentType.jsonl]: 'jsonl',
  [ContentType.any]: 'jsonl',
  [ContentType.json]: 'json',
};

export type TableUpdateSpec = TableAppendSpec | TablePopSpec | TableInsertSpec;

export interface TableAppendSpec {
  append: {
    row: Record<string, any>;
  };
}

export interface TablePopSpec {
  pop: {
    index: number;
  };
}

export interface TableInsertSpec {
  insert: {
    index: number;
    row: Record<string, any>;
  };
}

export type TableUpdateReq = {
  project_id: string;
  base_digest: string;
  updates: TableUpdateSpec[];
};

export type TableUpdateRes = {
  digest: string;
  updated_row_digests: string[];
};

export type TableCreateReq = {
  table: TableSchemaForInsert;
};

export type TableSchemaForInsert = {
  project_id: string;
  rows: Array<Record<string, any>>;
};

export type TableCreateRes = {
  digest: string;
  row_digests: string[];
};

// Thread API types
export type ThreadSchema = {
  thread_id: string;
  turn_count: number;
  start_time: string;
  last_updated: string;
  first_turn_id?: string;
  last_turn_id?: string;
  p50_turn_duration_ms?: number;
  p99_turn_duration_ms?: number;
};

export type ThreadsQueryFilter = {
  after_datetime?: string;
  before_datetime?: string;
  thread_ids?: string[];
};

export type ThreadsQueryReq = {
  project_id: string;
  filter?: ThreadsQueryFilter;
  limit?: number;
  offset?: number;
  sort_by?: SortBy[];
};

export type ThreadsQueryRes = {
  threads: ThreadSchema[];
};

export type EvaluateModelReq = {
  project_id: string;
  evaluation_ref: string;
  model_ref: string;
};

export type EvaluateModelRes = {
  call_id: string;
};

export type RescoreEvaluationReq = {
  project_id: string;
  source_evaluation_run_id: string;
  scorer_refs: string[];
};

export type RescoreEvaluationRes = {
  call_id: string;
  evaluation_run_id: string;
};

export type EvaluationStatusReq = {
  project_id: string;
  call_id: string;
};

export type EvaluationStatusNotFound = {
  code: 'not_found';
};

export type EvaluationStatusRunning = {
  code: 'running';
  completed_rows: number;
  total_rows: number;
};

export type EvaluationStatusFailed = {
  code: 'failed';
  error?: string | null;
};

export type EvaluationStatusComplete = {
  code: 'complete';
  output: {[key: string]: any};
};

export type EvaluationStatus =
  | EvaluationStatusNotFound
  | EvaluationStatusRunning
  | EvaluationStatusFailed
  | EvaluationStatusComplete;

export type EvaluationStatusRes = {
  status: EvaluationStatus;
};

export type CallsScoreReq = {
  project_id: string;
  call_ids: string[];
  scorer_refs: string[];
};

export type CallsScoreRes = Record<string, never>;

export type ImageGenerationCreateInputs = {
  model: string;
  prompt: string;
  n?: number;
};

export type ImageGenerationCreateReq = {
  project_id: string;
  inputs: ImageGenerationCreateInputs;
  track_llm_call?: boolean;
};

export type ImageGenerationCreateRes = {
  response: any;
  weave_call_id?: string;
};

// Annotation Queue API types
export type AnnotationQueueSchema = {
  id: string; // UUID
  project_id: string;
  name: string;
  description: string;
  scorer_refs: string[]; // Array of weave:// refs to scorers
  created_at: string;
  created_by: string; // wb_user_id
  updated_at: string;
  deleted_at?: string | null;
};

export type AnnotationQueueCreateReq = {
  project_id: string;
  name: string;
  description?: string;
  scorer_refs: string[];
  wb_user_id?: string | null;
};

export type AnnotationQueueCreateRes = {
  id: string; // UUID of the created queue
};

export type AnnotationQueuesQueryReq = {
  project_id: string;
  name?: string | null; // Filter by queue name (case-insensitive partial match)
  limit?: number | null;
  offset?: number | null;
};

export type AnnotationQueuesQueryRes = {
  queues: AnnotationQueueSchema[];
};

export type AnnotationQueueReadReq = {
  project_id: string;
  queue_id: string;
};

export type AnnotationQueueReadRes = {
  queue: AnnotationQueueSchema;
};

export type AnnotationQueueDeleteReq = {
  project_id: string;
  queue_id: string;
};

export type AnnotationQueueDeleteRes = {
  queue: AnnotationQueueSchema;
};

export type AnnotationQueueUpdateReq = {
  project_id: string;
  queue_id: string;
  name?: string | null;
  description?: string | null;
  scorer_refs?: string[] | null;
};

export type AnnotationQueueUpdateRes = {
  queue: AnnotationQueueSchema;
};

export type AnnotationState =
  | 'unstarted'
  | 'in_progress'
  | 'completed'
  | 'skipped';

export type AnnotationQueueItemSchema = {
  id: string; // UUID
  project_id: string;
  queue_id: string; // UUID
  call_id: string;
  call_started_at: string;
  call_ended_at?: string | null;
  call_op_name: string;
  call_trace_id: string;
  display_fields: string[]; // JSON paths like ['input.prompt', 'output.text']
  added_by?: string | null; // wb_user_id (nullable)
  annotation_state: AnnotationState;
  annotator_user_id?: string | null; // wb_user_id of annotator who owns the most recent state (nullable)
  created_at: string;
  created_by: string; // wb_user_id
  updated_at: string;
  deleted_at?: string | null;
  position_in_queue?: number | null; // 1-based position in full queue (only present if include_position was requested)
};

export type AnnotationQueueAddCallsBody = {
  project_id: string;
  call_ids: string[];
  display_fields: string[]; // JSON paths to display to annotators
};

export interface AnnotationQueueAddCallsReq
  extends AnnotationQueueAddCallsBody {
  queue_id: string;
}

export type AnnotationQueueAddCallsRes = {
  added_count: number; // Number of calls successfully added
  duplicates: number; // Number of calls already in queue (skipped)
};

export type AnnotationQueueItemsFilter = {
  call_id?: string | null; // Filter by exact call ID
  call_op_name?: string | null; // Filter by exact operation name
  call_trace_id?: string | null; // Filter by exact trace ID
  added_by?: string | null; // Filter by W&B user ID who added the call
  annotation_states?: AnnotationState[] | null; // Filter by annotation states
};

export type AnnotationQueueItemsQueryBody = {
  project_id: string;
  filter?: AnnotationQueueItemsFilter | null;
  sort_by?: Array<{field: string; direction: 'asc' | 'desc'}> | null;
  limit?: number | null;
  offset?: number | null;
  include_position?: boolean; // Include position_in_queue field (1-based index in full queue)
};

export interface AnnotationQueueItemsQueryReq
  extends AnnotationQueueItemsQueryBody {
  queue_id: string;
}

export type AnnotationQueueItemsQueryRes = {
  items: AnnotationQueueItemSchema[];
};

export type AnnotationQueueStatsSchema = {
  queue_id: string;
  total_items: number;
  completed_items: number;
};

export type AnnotationQueuesStatsReq = {
  project_id: string;
  queue_ids: string[];
};

export type AnnotationQueuesStatsRes = {
  stats: AnnotationQueueStatsSchema[];
};

export type AnnotationQueueItemProgressUpdateBody = {
  project_id: string;
  annotation_state: Exclude<AnnotationState, 'unstarted'>; // 'in_progress' | 'completed' | 'skipped'
};

export interface AnnotatorQueueItemsProgressUpdateReq
  extends AnnotationQueueItemProgressUpdateBody {
  queue_id: string;
  item_id: string;
}

export type AnnotatorQueueItemsProgressUpdateRes = {
  item: AnnotationQueueItemSchema;
};

// --- Usage aggregation endpoints ---

/**
 * Aggregated usage metrics for a specific LLM.
 */
export type LLMAggregatedUsage = {
  requests: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  // Cost fields - only populated when include_costs=True
  prompt_tokens_total_cost?: number;
  completion_tokens_total_cost?: number;
  cache_read_input_tokens_total_cost?: number;
  cache_creation_input_tokens_total_cost?: number;
};

/**
 * Request to compute per-call usage for a trace, with descendant rollup.
 * This endpoint returns usage metrics for each call in the trace, where each
 * call's metrics include the sum of its own usage plus all descendants' usage.
 * Use this for trace view where you want to see rolled-up metrics per call.
 */
export type TraceUsageReq = {
  project_id: string;
  filter?: TraceCallsFilter;
  query?: Query;
  include_costs?: boolean;
};

/**
 * Response with per-call usage metrics (each includes descendant contributions).
 */
export type TraceUsageRes = {
  call_usage: {[callId: string]: {[llmId: string]: LLMAggregatedUsage}};
};

/**
 * Request to compute aggregated usage for multiple root calls.
 * This endpoint returns usage metrics for each requested root call, where each
 * root's metrics include the sum of its own usage plus all descendants' usage.
 */
export type CallsUsageReq = {
  project_id: string;
  call_ids: string[];
  include_costs?: boolean;
  /** Maximum number of calls to process across all traces. Default 100,000. */
  limit?: number;
};

/**
 * Response with aggregated usage metrics per root call.
 * Same shape as TraceUsageRes.
 */
export type CallsUsageRes = TraceUsageRes;

export type AggregationType = 'sum' | 'avg' | 'min' | 'max' | 'count';

export type UsageMetric =
  | 'input_tokens'
  | 'output_tokens'
  | 'total_tokens'
  | 'input_cost'
  | 'output_cost'
  | 'total_cost';

export type UsageMetricSpec = {
  metric: UsageMetric;
  aggregations?: AggregationType[];
  percentiles?: number[];
};

export type CallMetric = 'latency_ms' | 'call_count' | 'error_count';

export type CallMetricSpec = {
  metric: CallMetric;
  aggregations?: AggregationType[];
  percentiles?: number[];
};

export type CallStatsReq = {
  project_id: string;
  start?: string;
  end?: string;
  granularity?: number;
  usage_metrics?: UsageMetricSpec[];
  call_metrics?: CallMetricSpec[];
  filter?: TraceCallsFilter;
  timezone?: string;
};

export type UsageBucket = {
  timestamp: string;
  model: string;
  count: number;
  sum_input_tokens?: number;
  sum_output_tokens?: number;
  sum_total_tokens?: number;
  sum_input_cost?: number;
  sum_output_cost?: number;
  sum_total_cost?: number;
  avg_input_tokens?: number;
  avg_output_tokens?: number;
  avg_total_tokens?: number;
  avg_input_cost?: number;
  avg_output_cost?: number;
  avg_total_cost?: number;
  [key: string]: string | number | undefined;
};

export type CallBucket = {
  timestamp: string;
  count: number;
  sum_latency_ms?: number;
  avg_latency_ms?: number;
  max_latency_ms?: number;
  sum_call_count?: number;
  sum_error_count?: number;
  [key: string]: string | number | undefined;
};

export type CallStatsRes = {
  start: string;
  end: string;
  granularity: number;
  timezone: string;
  usage_buckets: UsageBucket[];
  call_buckets: CallBucket[];
};

export interface AgentSortBy {
  field: string;
  direction: 'asc' | 'desc';
}

export interface AgentSchema {
  project_id: string;
  agent_name: string;
  invocation_count: number;
  span_count: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_duration_ms: number;
  error_count: number;
  first_seen: string | null;
  last_seen: string | null;
}

export interface AgentsQueryFilters {
  agent_name?: string;
}

export type AgentsQueryReq = {
  project_id: string;
  filters?: AgentsQueryFilters;
  sort_by?: AgentSortBy[];
  limit?: number;
  offset?: number;
};

export type AgentsQueryRes = {
  agents: AgentSchema[];
  total_count: number;
};

export type AgentCustomAttrSource =
  | 'custom_attrs_string'
  | 'custom_attrs_int'
  | 'custom_attrs_float'
  | 'custom_attrs_bool';

export type AgentCustomAttrValueType = 'string' | 'int' | 'float' | 'bool';

export type AgentCustomAttrSchemaItem = {
  source: AgentCustomAttrSource;
  key: string;
  value_type: AgentCustomAttrValueType;
  span_count: number;
};

export type AgentCustomAttrsSchemaReq = {
  project_id: string;
  query?: Query | null;
  started_after?: string | null;
  started_before?: string | null;
  limit?: number;
  offset?: number;
};

export type AgentCustomAttrsSchemaRes = {
  attributes: AgentCustomAttrSchemaItem[];
  limit: number;
  offset: number;
  has_more: boolean;
};

export type AgentSpanGroupDistributionSpec = {
  alias: string;
  value: AgentSpanValueRef;
  bins?: number;
  top_n?: number;
};

export type AgentSpanGroupDistributionBin = {
  index: number;
  min: number;
  max: number;
  count: number;
};

export type AgentSpanGroupDistributionValue = {
  value: string;
  count: number;
};

export type AgentSpanGroupDistributionItem = {
  alias: string;
  source: AgentCustomAttrSource;
  key: string;
  value_type: AgentCustomAttrValueType;
  total_count: number;
  present_count: number;
  missing_count: number;
  other_count: number;
  bins: AgentSpanGroupDistributionBin[];
  values: AgentSpanGroupDistributionValue[];
};

export interface AgentVersionsQueryReq {
  project_id: string;
  agent_name: string;
  sort_by?: AgentSortBy[];
  limit?: number;
  offset?: number;
}

export interface AgentVersionSchema extends AgentSchema {
  agent_version: string;
}

export type AgentVersionsQueryRes = {
  versions: AgentVersionSchema[];
  total_count: number;
};

export interface AgentGroupByRef {
  source?:
    | 'column'
    | 'custom_attrs_string'
    | 'custom_attrs_int'
    | 'custom_attrs_float'
    | 'custom_attrs_bool';
  key: string;
  alias?: string;
}

export type AgentSpanStatsValueType =
  | 'datetime'
  | 'number'
  | 'boolean'
  | 'string';

export type AgentSpanStatsColumnValueType =
  | 'datetime'
  | AgentSpanStatsValueType;

export type AgentSpanStatsAggregation =
  | 'sum'
  | 'avg'
  | 'min'
  | 'max'
  | 'count'
  | 'count_distinct'
  | 'count_true'
  | 'count_false';

export type AgentSpanValueRef = {
  source:
    | 'field'
    | 'derived'
    | 'custom_attrs_string'
    | 'custom_attrs_int'
    | 'custom_attrs_float'
    | 'custom_attrs_bool';
  key: string;
};

export type AgentSpanStatsMetricSpec = {
  alias: string;
  value_type: AgentSpanStatsValueType;
  aggregations?: AgentSpanStatsAggregation[];
  percentiles?: number[];
  value: AgentSpanValueRef;
};

export type AgentSpanMeasureSpec = {
  alias: string;
  aggregation: AgentSpanStatsAggregation;
  value?: AgentSpanValueRef | null;
  value_type?: AgentSpanStatsValueType | null;
  filter?: Query | null;
};

export type AgentSpanStatsColumn = {
  name: string;
  role: 'time' | 'bucket' | 'group' | 'metric';
  value_type: AgentSpanStatsColumnValueType;
  metric?: string | null;
  aggregation?: string | null;
};

export type AgentSpanStatsTimeBucketSpec = {
  type: 'time';
};

export type AgentSpanStatsNumericBucketSpec = {
  type: 'number';
  alias?: string;
  bins?: number;
  min?: number | null;
  max?: number | null;
  value?: AgentSpanValueRef | null;
  group_by?: AgentGroupByRef[];
  measure?: AgentSpanMeasureSpec | null;
};

export type AgentSpanStatsBucketSpec =
  | AgentSpanStatsTimeBucketSpec
  | AgentSpanStatsNumericBucketSpec;

export type AgentSpanStatsReq = {
  project_id: string;
  query?: Query;
  start: string;
  end?: string;
  granularity?: number;
  timezone?: string;
  group_by?: AgentGroupByRef[];
  metrics: AgentSpanStatsMetricSpec[];
  group_limit?: number;
  bucket_by?: AgentSpanStatsBucketSpec | null;
  group_filters?: AgentSpanGroupFilter[];
};

export type AgentSpanStatsRes = {
  start: string;
  end: string;
  granularity?: number | null;
  timezone: string;
  bucket_type?: 'time' | 'number';
  columns: AgentSpanStatsColumn[];
  rows: Array<Record<string, string | number | boolean | null>>;
};

export interface AgentConversationMessagePreview {
  role: string;
  text: string;
}

export interface AgentSpanGroupRow {
  group_keys: Record<string, string | number | boolean | null>;
  span_count: number;
  invocation_count: number;
  conversation_count: number;
  total_input_tokens: number;
  total_cache_creation_input_tokens: number;
  total_cache_read_input_tokens: number;
  total_output_tokens: number;
  total_reasoning_tokens: number;
  total_duration_ms: number;
  error_count: number;
  agent_names: string[];
  agent_versions: string[];
  provider_names: string[];
  request_models: string[];
  conversation_names: string[];
  first_seen: string | null;
  last_seen: string | null;
  first_message: AgentConversationMessagePreview | null;
  last_message: AgentConversationMessagePreview | null;
  metrics: Record<string, string | number | boolean | null>;
  distributions: Record<string, AgentSpanGroupDistributionItem>;
}

export type AgentSpanGroupFilter = {
  group_by?: AgentGroupByRef[];
  measure: AgentSpanMeasureSpec;
  min?: number | string | null;
  max?: number | string | null;
};

export interface AgentSpanSchema {
  project_id: string;
  trace_id: string;
  span_id: string;
  parent_span_id: string | null;
  span_name: string | null;
  span_kind: string | null;
  started_at: string | null;
  ended_at: string | null;
  status_code: string | null;
  status_message: string | null;
  operation_name: string | null;
  provider_name: string | null;
  agent_name: string | null;
  agent_id: string | null;
  agent_description: string | null;
  agent_version: string | null;
  request_model: string | null;
  response_model: string | null;
  response_id: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  reasoning_tokens: number | null;
  cache_creation_input_tokens: number | null;
  cache_read_input_tokens: number | null;
  reasoning_content: string | null;
  conversation_id: string | null;
  conversation_name: string | null;
  tool_name: string | null;
  tool_type: string | null;
  tool_call_id: string | null;
  tool_description: string | null;
  tool_definitions: string | null;
  finish_reasons: string[];
  error_type: string | null;
  request_temperature: number | null;
  request_max_tokens: number | null;
  request_top_p: number | null;
  request_frequency_penalty: number | null;
  request_presence_penalty: number | null;
  request_seed: number | null;
  request_stop_sequences: string[];
  request_choice_count: number | null;
  output_type: string | null;
  input_messages: unknown[];
  output_messages: unknown[];
  system_instructions: string[];
  tool_call_arguments: string | null;
  tool_call_result: string | null;
  compaction_summary: string | null;
  compaction_items_before: number | null;
  compaction_items_after: number | null;
  content_refs: string[];
  artifact_refs: string[];
  object_refs: string[];
  custom_attrs_string: Record<string, string>;
  custom_attrs_int: Record<string, number>;
  custom_attrs_float: Record<string, number>;
  custom_attrs_bool: Record<string, boolean>;
  server_address: string | null;
  server_port: number | null;
  wb_user_id: string | null;
  wb_run_id: string | null;
  wb_run_step: number | null;
  wb_run_step_end: number | null;
  // Only present when include_details is set on the query; raw OTel JSON for
  // the span, rendered in the span detail panel's Attributes tab.
  raw_span_dump: string | null;
}

export type AgentSpansQueryReq = {
  project_id: string;
  query?: Query;
  group_by?: AgentGroupByRef[];
  measures?: AgentSpanMeasureSpec[];
  group_filters?: AgentSpanGroupFilter[];
  group_distributions?: AgentSpanGroupDistributionSpec[];
  custom_attr_columns?: AgentSpanValueRef[];
  include_details?: boolean;
  sort_by?: AgentSortBy[];
  limit?: number;
  offset?: number;
  started_after?: string;
  started_before?: string;
};

export type AgentSpansQueryRes = {
  spans: AgentSpanSchema[];
  groups: AgentSpanGroupRow[];
  total_count: number;
};

export type AgentChatMessageType =
  | 'user_message'
  | 'assistant_message'
  | 'tool_call'
  | 'agent_handoff'
  | 'agent_start'
  | 'context_compacted';

export interface AgentChatUserMessage {
  text: string;
  content_refs: string[];
}

export interface AgentChatAssistantMessage {
  text: string;
  model: string | null;
  reasoning_content: string | null;
  reasoning_tokens: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_creation_input_tokens?: number | null;
  cache_read_input_tokens?: number | null;
  duration_ms: number | null;
  status: string | null;
  content_refs: string[];
}

export interface AgentChatToolCall {
  tool_name: string | null;
  tool_arguments: string | null;
  tool_result: string | null;
  duration_ms: number | null;
  status: string | null;
  content_refs: string[];
}

export interface AgentChatAgentStart {
  model: string | null;
  system_instructions: string | null;
  tool_definitions: string | null;
  status: string | null;
}

export type AgentChatAgentHandoff = Record<string, never>;

export interface AgentChatContextCompacted {
  compaction_summary: string | null;
  compaction_items_before: number | null;
  compaction_items_after: number | null;
}

export interface AgentChatMessage {
  type: AgentChatMessageType;
  span_id: string | null;
  agent_name: string | null;
  started_at: string | null;
  user_message?: AgentChatUserMessage | null;
  assistant_message?: AgentChatAssistantMessage | null;
  tool_call?: AgentChatToolCall | null;
  agent_start?: AgentChatAgentStart | null;
  agent_handoff?: AgentChatAgentHandoff | null;
  context_compacted?: AgentChatContextCompacted | null;
  // Populated when the chat-view request sets ``include_feedback=true``;
  // attached to the per-message ``agent_span`` ref.
  feedback?: Feedback[] | null;
}

export type AgentTraceChatReq = {
  project_id: string;
  trace_id: string;
  include_feedback?: boolean;
};

export type AgentTraceChatRes = {
  trace_id: string;
  root_span_name?: string | null;
  provider?: string | null;
  total_duration_ms?: number | null;
  messages: AgentChatMessage[];
  // Feedback attached to the ``agent_turn`` ref for this trace.
  feedback?: Feedback[] | null;
};

export type AgentConversationChatReq = {
  project_id: string;
  conversation_id: string;
  limit?: number;
  offset?: number;
  include_feedback?: boolean;
};

export type AgentConversationChatRes = {
  conversation_id: string;
  turns: AgentTraceChatRes[];
  total_turns: number;
  has_more: boolean;
  limit: number;
  offset: number;
  // Feedback attached to the ``agent_conversation`` ref.
  feedback?: Feedback[] | null;
};

export type SearchMessageRole =
  | ''
  | 'user'
  | 'assistant'
  | 'system'
  | 'tool'
  | 'tool_call'
  | 'tool_result';

export interface AgentSearchMatchedMessage {
  span_id: string;
  trace_id: string;
  role: SearchMessageRole;
  content_preview: string;
  content_digest: string;
  started_at: string;
}

export interface AgentSearchConversationResult {
  conversation_id: string;
  conversation_name: string;
  agent_name: string;
  matched_messages: AgentSearchMatchedMessage[];
  last_activity: string;
}

export type AgentSearchReq = {
  project_id: string;
  query: string;
  roles?: SearchMessageRole[];
  conversation_id?: string;
  agent_name?: string;
  provider_name?: string;
  request_model?: string;
  started_after?: string;
  started_before?: string;
  limit?: number;
  offset?: number;
};

export type AgentSearchRes = {
  results: AgentSearchConversationResult[];
  total_conversations: number;
};
